import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Search, Link as LinkIcon, Eye, X, Pencil, Upload } from "lucide-react";
import { confirmAlert } from "../../../components/ui/confirmAlert";
import { handleGet, handleRemove, handleUpdate } from "../../../controllers/controller.all";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

export default function TeacherList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);

    const openTeacherClasses = async (teacher) => {
        setSelectedTeacher(teacher);
        setIsModalOpen(true);
        fetchTeacherClasses(teacher._id);
    };

    const fetchTeacherClasses = async (profId) => {
        setLoadingClasses(true);
        await handleGet(`/prof_classe/prof/${profId}`, (res) => {
            if (res?.prof_classes) {
                setTeacherClasses(res.prof_classes);
            }
        }, token);
        setLoadingClasses(false);
    };

    const handleDeaffect = async (profClasseId) => {
        await handleRemove(`/prof_classe/${profClasseId}`, () => {
            if (selectedTeacher) {
                fetchTeacherClasses(selectedTeacher._id);
            }
        }, token, { message: "Voulez-vous vraiment désaffecter complètement cette classe ?" });
    };

    const handleRemoveMatiere = async (item, matiereIdToRemove) => {
        const newMatieres = item.matieres.filter(m => m._id !== matiereIdToRemove).map(m => m._id);
        
        if (newMatieres.length === 0) {
            await handleRemove(`/prof_classe/${item._id}`, () => {
                if (selectedTeacher) {
                    fetchTeacherClasses(selectedTeacher._id);
                }
            }, token, { message: "C'est la dernière matière. Voulez-vous désaffecter complètement le professeur de cette classe ?" });
            return;
        }

        const payload = {
            prof: item.prof?._id || item.prof || selectedTeacher._id,
            classe: item.classe?._id || item.classe,
            matieres: newMatieres,
            ecole: ecoleId
        };

        const res = await handleUpdate(null, `/prof_classe/${item._id}`, payload, null, token);
        if (res) {
            fetchTeacherClasses(selectedTeacher._id);
        }
    };

    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("nom");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 20;

    const fetchTeachers = async () => {
        setLoading(true);
        await handleGet(`/user/s/all/professeurs/${ecoleId}?skip=0&limit=100000`, (res) => {
            if (res?.users) {
                setTeachers(res.users);
            }
        }, token);
        setLoading(false);
    };

    useEffect(() => {
        if(token) fetchTeachers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);
    
    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const handleDelete = async (id) => {
        const isConfirmed = await confirmAlert({
            title: "Supprimer le professeur",
            message: "Voulez-vous vraiment supprimer ce professeur ? Ses affectations de classes seront également supprimées.",
            confirmText: "Supprimer",
            type: "danger"
        });
        if (!isConfirmed) return;

        try {
            // 1. Fetch all prof_classe associated with this teacher
            let profClasses = [];
            await handleGet(`/prof_classe/prof/${id}`, (res) => {
                if (res?.prof_classes) profClasses = res.prof_classes;
            }, token);

            // 2. Delete each prof_classe silently (no confirmation needed)
            for (const pc of profClasses) {
                await handleRemove(`/prof_classe/${pc._id}`, () => {}, token, { skipConfirm: true });
            }

            // 3. Delete the teacher
            await handleRemove(`/user/${id}`, () => {}, token, { skipConfirm: true });
            fetchTeachers();
        } catch (error) {
            console.error(error);
        }
    };

    const filteredTeachers = teachers.filter(t => !search || 
        t.prenom?.toLowerCase().includes(search.toLowerCase()) || 
        t.nom?.toLowerCase().includes(search.toLowerCase()) || 
        t.tel?.toLowerCase().includes(search.toLowerCase()) ||
        t.email?.toLowerCase().includes(search.toLowerCase())
    );

    const sortedTeachers = [...filteredTeachers].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginatedTeachers = sortedTeachers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Professeurs</h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez le corps professoral de l'établissement.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Link 
                        to="/teachers/batch" 
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 rounded-lg transition"
                    >
                        <Upload className="h-4 w-4" />
                        <span className="hidden sm:inline">Import en masse</span>
                    </Link>
                    <Link 
                        to="/teachers/affect" 
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 rounded-lg transition"
                    >
                        <LinkIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Affecter Classe</span>
                    </Link>
                    <Link 
                        to="/teachers/create" 
                        className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                    >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Nouveau Professeur</span>
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="relative max-w-sm w-full text-gray-400">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" />
                        <input 
                            type="text" 
                            placeholder="Rechercher..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <SortableHeader label="Nom & Prénom" sortKey="nom" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Contact" sortKey="tel" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="3" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : paginatedTeachers.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucun professeur trouvé.</td>
                                </tr>
                            ) : (
                                paginatedTeachers.map((teacher) => (
                                    <tr key={teacher._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{teacher.prenom} {teacher.nom}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{teacher.tel}</div>
                                            <div className="text-sm text-gray-500 block truncate max-w-xs">{teacher.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button 
                                                onClick={() => openTeacherClasses(teacher)}
                                                className="text-indigo-500 hover:text-indigo-700 bg-indigo-50 p-2 rounded-lg transition mr-2"
                                                title="Voir les classes affectées"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <Link 
                                                to={`/teachers/update/${teacher._id}`}
                                                className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 p-2 rounded-lg transition inline-block mr-2"
                                                title="Modifier le profil"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Link>
                                            <button 
                                                onClick={() => handleDelete(teacher._id)}
                                                className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                title="Supprimer le professeur"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filteredTeachers.length > itemsPerPage && (
                    <Pagination 
                        currentPage={currentPage} 
                        totalItems={filteredTeachers.length} 
                        itemsPerPage={itemsPerPage} 
                        onPageChange={setCurrentPage} 
                    />
                )}
            </div>

            {/* Modal for Teacher Classes */}
            {isModalOpen && selectedTeacher && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-4 px-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                                Classes de {selectedTeacher.prenom} {selectedTeacher.nom}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg transition">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-0 overflow-y-auto w-full">
                            {loadingClasses ? (
                                <div className="text-center text-gray-500 py-10">Chargement des données...</div>
                            ) : teacherClasses.length === 0 ? (
                                <div className="text-center text-gray-500 py-10">Aucune classe n'est affectée à ce professeur.</div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classe</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matières</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {teacherClasses.map((item) => (
                                            <tr key={item._id} className="hover:bg-gray-50 transition">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {item.classe?.libelle || "Classe introuvable"}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    <div className="flex flex-wrap gap-2">
                                                        {item.matieres && item.matieres.length > 0 
                                                            ? item.matieres.map(m => (
                                                                <span key={m._id} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                                                    {m.libelle}
                                                                    <button 
                                                                        onClick={() => handleRemoveMatiere(item, m._id)}
                                                                        className="text-indigo-400 hover:text-red-500 hover:bg-indigo-100 rounded-full p-0.5 transition"
                                                                        title="Retirer cette matière"
                                                                    >
                                                                        <X className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </span>
                                                            )) 
                                                            : <span className="italic text-gray-400">Aucune matière</span>
                                                        }
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                    <button 
                                                        onClick={() => handleDeaffect(item._id)}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg border border-transparent hover:bg-red-50 transition"
                                                        title="Retirer la classe entière"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="p-4 px-6 border-t border-gray-200 bg-gray-50 flex justify-end">
                            <button 
                                onClick={() => setIsModalOpen(false)} 
                                className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition shadow-sm"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
